import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSubscription } from '../hooks/useSubscription'
import { useAuth } from '../hooks/useSupabaseAuth'
import { Copy, Check, ArrowLeft, CreditCard, Clock, AlertCircle, CheckCircle } from 'lucide-react'
import { componentClasses, cn } from '../utils/designSystem'

const PaymentConfirmation = ({ orderId: propOrderId }) => {
  const { orderId: paramOrderId } = useParams()
  const orderId = propOrderId || paramOrderId
  const navigate = useNavigate()
  const { getTransaction } = useSubscription()
  const { user } = useAuth()
  const [transaction, setTransaction] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [copiedField, setCopiedField] = useState(null)

  // Bank account details
  const bankDetails = {
    bankName: 'BCA (Bank Central Asia)',
    accountNumber: '8610274642',
    accountName: 'Galih Wicaksono'
  }

  useEffect(() => {
    const loadTransaction = async () => {
      try {
        setLoading(true)
        const { data, error } = await getTransaction(orderId)
        
        if (error) {
          setError('Transaksi tidak ditemukan')
        } else {
          setTransaction(data)
        }
      } catch (err) {
        setError('Terjadi kesalahan saat memuat data transaksi')
      } finally {
        setLoading(false)
      }
    }

    if (orderId) {
      loadTransaction()
    }
  }, [orderId, getTransaction])

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(price)
  }

  const getPackageColor = (packageName) => {
    switch (packageName) {
      case 'STARTER':
        return 'from-blue-500 to-blue-600'
      case 'MEDIUM':
        return 'from-purple-500 to-purple-600'
      case 'LARGE':
        return 'from-orange-500 to-orange-600'
      default:
        return 'from-gray-500 to-gray-600'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400'
      case 'success':
        return 'bg-green-500/20 text-green-400'
      case 'cancelled':
        return 'bg-red-500/20 text-red-400'
      case 'failed':
        return 'bg-red-500/20 text-red-400'
      default:
        return 'bg-gray-500/20 text-gray-400'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Menunggu Pembayaran'
      case 'success':
        return 'Berhasil'
      case 'cancelled':
        return 'Dibatalkan'
      case 'failed':
        return 'Gagal'
      default:
        return status
    }
  }

  if (loading) {
    return (
      <div className={cn(componentClasses.main, "min-h-screen flex items-center justify-center")}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    )
  }

  if (error || !transaction) {
    return (
      <div className={cn(componentClasses.main, "min-h-screen flex items-center justify-center")}>
        <div className="bg-bg-tertiary border border-border-primary rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-text-primary mb-2">Transaksi Tidak Ditemukan</h2>
            <p className="text-text-secondary mb-6">{error}</p>
            <button
              onClick={() => navigate('/subscription')}
              className="bg-brand-primary text-text-primary px-6 py-2 rounded-lg hover:bg-brand-secondary transition-colors"
            >
              Kembali ke Paket
            </button>
          </div>
        </div>
      </div>
    )
  }

  const packageData = transaction.subscription_packages

  return (
    <div className={cn(componentClasses.main, "min-h-screen py-8")}>
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/subscription')}
            className="flex items-center text-text-tertiary hover:text-text-primary mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Kembali ke Paket Berlangganan</span>
          </button>
          <h1 className="text-3xl font-bold text-text-primary">Konfirmasi Pembayaran</h1>
          <p className="text-text-secondary mt-2">
            Silakan lakukan pembayaran sesuai instruksi di bawah ini
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Package Details */}
          <div className="bg-bg-tertiary border border-border-primary rounded-2xl shadow-lg overflow-hidden">
            <div className={`bg-gradient-to-r ${getPackageColor(packageData.name)} text-white p-6`}>
              <div className="flex items-center justify-center mb-4">
                <CreditCard className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold text-center mb-2">{packageData.name}</h2>
              <div className="text-center">
                <span className="text-3xl font-bold">{formatPrice(transaction.amount)}</span>
                <span className="text-sm opacity-90">/30 hari</span>
              </div>
              <div className="text-center mt-2">
                <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-semibold">
                  {packageData.credits.toLocaleString('id-ID')} Kredit
                </span>
              </div>
            </div>

            <div className="p-6">
              <h3 className="font-semibold text-text-primary mb-4">Detail Transaksi</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-secondary">Order ID:</span>
                  <span className="font-mono font-semibold text-text-primary">{transaction.order_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(transaction.status)}`}>
                    {getStatusText(transaction.status)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Tanggal:</span>
                  <span className="text-text-primary">{new Date(transaction.created_at).toLocaleDateString('id-ID')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Metode Pembayaran:</span>
                  <span className="text-text-primary">Transfer Bank</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Instructions */}
          <div className="bg-bg-tertiary border border-border-primary rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-text-primary mb-6">Instruksi Pembayaran</h2>
            
            {/* Bank Details */}
            <div className="bg-bg-quaternary rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-text-primary mb-4">Detail Rekening Tujuan</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Nama Bank
                  </label>
                  <div className="flex items-center justify-between bg-bg-tertiary border border-border-primary rounded-lg px-4 py-3">
                    <span className="font-semibold text-text-primary">{bankDetails.bankName}</span>
                    <button
                      onClick={() => copyToClipboard(bankDetails.bankName, 'bankName')}
                      className="flex items-center space-x-1 text-brand-primary hover:text-brand-secondary transition-colors"
                    >
                      {copiedField === 'bankName' ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                      <span className="text-sm">
                        {copiedField === 'bankName' ? 'Tersalin' : 'Copy'}
                      </span>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Nomor Rekening
                  </label>
                  <div className="flex items-center justify-between bg-bg-tertiary border border-border-primary rounded-lg px-4 py-3">
                    <span className="font-mono font-semibold text-lg text-text-primary">{bankDetails.accountNumber}</span>
                    <button
                      onClick={() => copyToClipboard(bankDetails.accountNumber, 'accountNumber')}
                      className="flex items-center space-x-1 text-brand-primary hover:text-brand-secondary transition-colors"
                    >
                      {copiedField === 'accountNumber' ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                      <span className="text-sm">
                        {copiedField === 'accountNumber' ? 'Tersalin' : 'Copy'}
                      </span>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Nama Penerima
                  </label>
                  <div className="flex items-center justify-between bg-bg-tertiary border border-border-primary rounded-lg px-4 py-3">
                    <span className="font-semibold text-text-primary">{bankDetails.accountName}</span>
                    <button
                      onClick={() => copyToClipboard(bankDetails.accountName, 'accountName')}
                      className="flex items-center space-x-1 text-brand-primary hover:text-brand-secondary transition-colors"
                    >
                      {copiedField === 'accountName' ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                      <span className="text-sm">
                        {copiedField === 'accountName' ? 'Tersalin' : 'Copy'}
                      </span>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Jumlah Transfer
                  </label>
                  <div className="flex items-center justify-between bg-bg-tertiary border border-border-primary rounded-lg px-4 py-3">
                    <span className="font-bold text-xl text-green-400">
                      {formatPrice(transaction.amount)}
                    </span>
                    <button
                      onClick={() => copyToClipboard(transaction.amount.toString(), 'amount')}
                      className="flex items-center space-x-1 text-brand-primary hover:text-brand-secondary transition-colors"
                    >
                      {copiedField === 'amount' ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                      <span className="text-sm">
                        {copiedField === 'amount' ? 'Tersalin' : 'Copy'}
                      </span>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Berita Transfer
                  </label>
                  <div className="flex items-center justify-between bg-bg-tertiary border border-border-primary rounded-lg px-4 py-3">
                    <span className="font-semibold text-text-primary">{user?.email || 'Email tidak tersedia'}</span>
                    <button
                      onClick={() => copyToClipboard(user?.email || '', 'transferNote')}
                      className="flex items-center space-x-1 text-brand-primary hover:text-brand-secondary transition-colors"
                    >
                      {copiedField === 'transferNote' ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                      <span className="text-sm">
                        {copiedField === 'transferNote' ? 'Tersalin' : 'Copy'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="space-y-4">
              <h3 className="font-semibold text-text-primary">Langkah-langkah Transfer:</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-text-secondary">
                <li>Buka aplikasi mobile banking atau kunjungi ATM terdekat</li>
                <li>Pilih menu transfer ke rekening BCA</li>
                <li>Masukkan nomor rekening tujuan: <strong className="text-text-primary">{bankDetails.accountNumber}</strong></li>
                <li>Masukkan jumlah transfer: <strong className="text-text-primary">{formatPrice(transaction.amount)}</strong></li>
                <li>Pastikan nama penerima sesuai: <strong className="text-text-primary">{bankDetails.accountName}</strong></li>
                <li>Masukkan berita transfer: <strong className="text-text-primary">{user?.email || 'Email Anda'}</strong></li>
                <li>Lakukan transfer dan simpan bukti transfer</li>
                <li>Tunggu konfirmasi dari admin (maksimal 1x24 jam)</li>
              </ol>
            </div>

            {/* Status Info */}
            <div className="mt-6 p-4 bg-brand-primary/10 border border-brand-primary/20 rounded-lg">
              <div className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-brand-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-text-primary">Status Pembayaran</h4>
                  <p className="text-sm text-text-secondary mt-1">
                    Paket berlangganan Anda akan diaktifkan secara otomatis setelah admin mengkonfirmasi pembayaran. 
                    Proses konfirmasi biasanya memakan waktu maksimal 1x24 jam pada hari kerja.
                  </p>
                </div>
              </div>
            </div>

            {/* WhatsApp Support */}
            <div className="mt-6">
              <button
                onClick={() => {
                  const message = `Konfirmasi Transfer BCA:

${user?.email || 'User'} - ${packageData.name} - BCA

Saya telah melakukan transfer untuk paket ${packageData.name} sebesar ${formatPrice(transaction.amount)} ke rekening BCA ${bankDetails.accountNumber} a.n ${bankDetails.accountName}.

Order ID: ${transaction.order_id}

Mohon untuk diverifikasi. Terima kasih.`
                  const encodedMessage = encodeURIComponent(message)
                  const whatsappUrl = `https://api.whatsapp.com/send/?phone=6288215741432&text=${encodedMessage}&type=phone_number&app_absent=0`
                  window.open(whatsappUrl, '_blank')
                }}
                className="w-full bg-green-500 text-white py-3 px-6 rounded-xl font-semibold hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                <span>Konfirmasi via WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentConfirmation